import { CMS_VIDEO_CONTAINER_SELECTOR, CMS_VIDEO_PLAYING_CLASS } from '~/utils/cmsContent'

/**
 * CMS video widget reveal.
 *
 * A widget that has a cover image keeps its YouTube player invisible (opacity 0) on top of
 * the cover, so the visitor sees our cover art but their click lands on the player and
 * starts it. The player is a cross-origin iframe, so the page never sees that click, and
 * `:focus` / `:focus-within` do not match the iframe. What the page can see is
 * `document.activeElement` becoming the iframe, and that is what reveals the player.
 *
 * Two ways focus reaches a player, measured in Chromium with cross-origin iframes:
 * - From the page: the window fires `blur`, with `activeElement` already the iframe.
 * - From another player: no event at all reaches the page (no window blur, no focusin,
 *   no focus on the iframe element, and for a touch tap no pointerover either), though
 *   `activeElement` still changes.
 * So once a blur leaves focus in an iframe, a short `activeElement` poll runs for as long
 * as focus stays in iframes, and reveals each player focus reaches. It stops when the
 * window takes focus back or focus is no longer in an iframe. Mouse, touch and keyboard
 * all go through the same path, and the poll holds no player, only reads activeElement.
 * Revealing is one-way: nothing removes the playing class, so a revealed player stays
 * visible (the ticket asks for no more). A "hide again" or "one player at a time" rule
 * would need its own teardown.
 *
 * The listeners are module-level and shared: one set however many CmsContent instances
 * are mounted, attached on the first mount and removed on the last unmount (an instance
 * that never mounted does not count down). The effect is
 * document-wide by nature (it reads `document.activeElement`), so per-instance listeners
 * would only multiply the same work. AB#64682
 */

const POLL_MS = 100

const focusIsInAnIframe = () => document.activeElement?.tagName === 'IFRAME'

const revealActivePlayer = () => {
  const active = document.activeElement
  if (active?.tagName !== 'IFRAME') { return }
  active.closest(CMS_VIDEO_CONTAINER_SELECTOR)?.classList.add(CMS_VIDEO_PLAYING_CLASS)
}

let subscribers = 0
let pollTimer: ReturnType<typeof setInterval> | undefined

const stopPoll = () => {
  clearInterval(pollTimer)
  pollTimer = undefined
}

const followFocusInIframes = () => {
  revealActivePlayer()
  if (pollTimer || !focusIsInAnIframe()) { return }
  pollTimer = setInterval(() => {
    if (!focusIsInAnIframe()) {
      stopPoll()
      return
    }
    revealActivePlayer()
  }, POLL_MS)
}

// Chromium already points activeElement at the iframe inside the blur handler; the
// deferred second read is a safeguard for browsers that update it late.
const onWindowBlur = () => {
  followFocusInIframes()
  setTimeout(followFocusInIframes, 0)
}

export const useCmsVideoReveal = () => {
  let mounted = false
  onMounted(() => {
    mounted = true
    if (subscribers++ > 0) { return }
    window.addEventListener('blur', onWindowBlur)
    window.addEventListener('focus', stopPoll)
  })
  onBeforeUnmount(() => {
    if (!mounted) { return }
    mounted = false
    if (--subscribers > 0) { return }
    window.removeEventListener('blur', onWindowBlur)
    window.removeEventListener('focus', stopPoll)
    stopPoll()
  })
}
